import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UsersIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  PageHeader,
  SkeletonRows,
  TextField,
  type Column,
} from '@csa/ui';
import { api } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';
import { UserDetailPanel } from './UserDetailPanel.js';
import type { UserListEntry } from './types.js';

/**
 * The user directory.
 *
 * Lists everyone who has ever signed in, including accounts holding no roles.
 * That is deliberate: provisioning on first sign-in with zero permissions is
 * what makes this list the complete access-review population, rather than
 * only the people an administrator remembered to add.
 */
export function UsersPage(): ReactElement {
  const tz = useTimeZone();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  // Debounce-free: the list is capped at 200 rows and the query is indexed, so
  // typing straight through is cheaper than the machinery to delay it.
  const users = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () =>
      api.get<{ users: UserListEntry[]; truncated: boolean }>(
        `/api/admin/users?q=${encodeURIComponent(search)}`,
      ),
  });

  const columns = useMemo<Column<UserListEntry>[]>(
    () => [
      {
        key: 'user',
        header: 'User',
        render: (user) => (
          <div className="flex min-w-0 flex-col">
            <span className="font-medium text-body">{user.displayName}</span>
            <span className="truncate text-xs text-muted">{user.upn}</span>
          </div>
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (user) =>
          user.roles.length === 0 ? (
            <Badge tone="warning">No access</Badge>
          ) : (
            <span className="flex flex-wrap gap-1">
              {user.roles.map((role) => (
                <Badge key={role} tone="neutral">
                  {role}
                </Badge>
              ))}
            </span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (user) =>
          user.enabled ? (
            <Badge tone="success">Active</Badge>
          ) : (
            <Badge tone="danger" title={user.disabledReason ?? undefined}>
              Disabled
            </Badge>
          ),
      },
      {
        key: 'lastSeen',
        header: `Last seen (${tz.timeZone})`,
        numeric: true,
        render: (user) => (
          // `dateTime` stays the raw ISO instant: that is the machine-readable
          // attribute, and a screen reader or a scraper should get the
          // unambiguous value rather than the rendered one.
          <time
            dateTime={user.lastSeenAt}
            className="text-xs text-muted"
            title={tz.titleFor(user.lastSeenAt)}
          >
            {tz.formatBare(user.lastSeenAt).value}
          </time>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (user) => (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setSelected(user.id)}
            aria-label={`Manage access for ${user.displayName}`}
          >
            Manage
          </Button>
        ),
      },
    ],
    [tz],
  );

  const withoutAccess = (users.data?.users ?? []).filter((u) => u.enabled && u.roles.length === 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Users"
        description="Everyone who has signed in. Signing in provisions an account but grants nothing until a role is assigned."
      />

      <div className="max-w-sm">
        <TextField
          label="Search"
          placeholder="Name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          type="search"
        />
      </div>

      {withoutAccess.length > 0 ? (
        <Alert tone="info" title={`${withoutAccess.length} account(s) awaiting access`}>
          These people have signed in but hold no roles, so they currently see an empty console.
        </Alert>
      ) : null}

      {users.data?.truncated ? (
        <Alert tone="warning" title="Showing the first 200 matches">
          Narrow the search to see the rest.
        </Alert>
      ) : null}

      {users.isPending ? (
        <SkeletonRows rows={6} />
      ) : users.error ? (
        <ErrorState
          title="Could not load users"
          message={users.error.message}
          action={<Button onClick={() => void users.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={users.data.users}
          rowKey={(user) => user.id}
          caption="Users and their assigned roles"
          empty={
            <EmptyState
              icon={UsersIcon}
              title={search ? 'No matching users' : 'Nobody has signed in yet'}
              description={
                search
                  ? 'Try a different name or email address.'
                  : 'Accounts appear here the first time someone signs in.'
              }
            />
          }
        />
      )}

      {selected ? <UserDetailPanel userId={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}
